import DOMPurify from 'dompurify';
import { imageUrl } from './setModel';

export const clamp = (value, min, max, fallback = min) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
};

export const darkness = (value, fallback = 0) => clamp(value, 0, 95, fallback) / 100;

export const backgroundStyle = (value, { size = 'cover', position = 'center', tileSize = 50 } = {}) => {
  const url = imageUrl(value);
  return url ? {
    backgroundImage: `url('${url}')`,
    backgroundPosition: position,
    backgroundRepeat: size === 'contain' ? 'repeat' : 'no-repeat',
    backgroundSize: size === 'contain' ? `${clamp(tileSize, 10, 100, 50)}% auto` : size,
  } : {};
};

export const backgroundValue = (data = {}, imageKey = 'background') => data.backgroundMode === 'color' ? '' : data[imageKey];

export const backgroundOverlay = (data = {}, fallback = 0) => data.backgroundMode === 'color' ? 0 : darkness(data.backgroundDarkness, fallback);

const rgb = (value, fallback = '#05070a') => {
  const hex = /^#[0-9a-f]{6}$/i.test(String(value)) ? String(value) : fallback;
  return [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16)).join(',');
};

export const gradientStyle = (data = {}, defaults = {}) => {
  const directions = { left: 'to left', right: 'to right', top: 'to top', bottom: 'to bottom' };
  const direction = directions[data.gradientDirection] || defaults.direction || 'to bottom';
  const strength = clamp(data.gradientStrength, 0, 100, defaults.strength ?? 75) / 100;
  const color = rgb(data.gradientColor, defaults.color || '#05070a');
  return { background: `linear-gradient(${direction}, rgba(${color}, ${strength}), rgba(${color}, 0))` };
};

const RICH_TEXT_SANITIZE_OPTIONS = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'ol', 'ul', 'li', 'h1', 'h2', 'h3', 'a'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
  ALLOW_DATA_ATTR: false,
};

export const cleanHtml = (value = '') => DOMPurify.sanitize(String(value), RICH_TEXT_SANITIZE_OPTIONS);

export const youtubeId = (value = '') => {
  const input = String(value).trim();
  if (!input) return '';
  if (/^[a-zA-Z0-9_-]{6,20}$/.test(input)) return input;
  try {
    const url = new URL(input);
    if (url.hostname.includes('youtu.be')) return url.pathname.split('/').filter(Boolean)[0] || '';
    if (url.hostname.includes('youtube.com')) return url.searchParams.get('v') || url.pathname.split('/').filter(Boolean).pop() || '';
  } catch { return ''; }
  return '';
};

export const externalLink = (value = '') => {
  const input = String(value).trim();
  if (!input) return '';
  if (input.startsWith('/') || input.startsWith('#')) return input;
  try {
    const url = new URL(input);
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : '';
  } catch { return ''; }
};
